import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// Filter selections held above the page-switch (in App.tsx) so they survive
// navigating away and back — e.g. opening a project and returning shouldn't
// reset the stage filter or the sponsor/people chips (issue #40).
// The task filters (projects / person / sponsor / collaborators) are shared by
// Organisation tâches and Planification des tâches, and also kept for the browser
// session so a reload doesn't lose them.
interface PageFiltersContextValue {
  portfolioStageFilter: string;
  setPortfolioStageFilter: (v: string) => void;
  pipelineFilterSponsors: string[];
  setPipelineFilterSponsors: (v: string[]) => void;
  pipelineFilterPeople: string[];
  setPipelineFilterPeople: (v: string[]) => void;
  taskFilterProjects: number[];
  setTaskFilterProjects: (v: number[]) => void;
  taskFilterPersons: string[];
  setTaskFilterPersons: (v: string[]) => void;
  taskFilterSponsors: string[];
  setTaskFilterSponsors: (v: string[]) => void;
  taskFilterCollabs: string[];
  setTaskFilterCollabs: (v: string[]) => void;
}

const PageFiltersContext = createContext<PageFiltersContextValue | null>(null);

/** useState that also lives in sessionStorage (survives a reload, not a new tab). */
function useSessionState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) as T : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
  }, [key, value]);
  return [value, setValue];
}

export function PageFiltersProvider({ children }: { children: ReactNode }) {
  const [portfolioStageFilter, setPortfolioStageFilter] = useState('active');
  const [pipelineFilterSponsors, setPipelineFilterSponsors] = useState<string[]>([]);
  const [pipelineFilterPeople, setPipelineFilterPeople] = useState<string[]>([]);
  const [taskFilterProjects, setTaskFilterProjects] = useSessionState<number[]>('pmo.taskFilter.projects', []);
  const [taskFilterPersons, setTaskFilterPersons] = useSessionState<string[]>('pmo.taskFilter.persons', []);
  const [taskFilterSponsors, setTaskFilterSponsors] = useSessionState<string[]>('pmo.taskFilter.sponsors', []);
  const [taskFilterCollabs, setTaskFilterCollabs] = useSessionState<string[]>('pmo.taskFilter.collabs', []);

  return (
    <PageFiltersContext.Provider value={{
      portfolioStageFilter, setPortfolioStageFilter,
      pipelineFilterSponsors, setPipelineFilterSponsors,
      pipelineFilterPeople, setPipelineFilterPeople,
      taskFilterProjects, setTaskFilterProjects,
      taskFilterPersons, setTaskFilterPersons,
      taskFilterSponsors, setTaskFilterSponsors,
      taskFilterCollabs, setTaskFilterCollabs,
    }}>
      {children}
    </PageFiltersContext.Provider>
  );
}

export function usePageFilters() {
  const ctx = useContext(PageFiltersContext);
  if (!ctx) throw new Error('usePageFilters must be used within PageFiltersProvider');
  return ctx;
}
